
import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getThemeDefaults, ThemeData } from '../logic/consultantTheme/themeDefaults';
import { API_CONFIG } from '../config/api';

const ConsultantThemePage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [themeData, setThemeData] = useState<ThemeData>(getThemeDefaults());
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'palette' | 'logo'>('palette');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [activeColorField, setActiveColorField] = useState<keyof ThemeData | null>(null);
  const colorPickerRef = useRef<HTMLInputElement>(null);


  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/consultant/theme`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        if (data.success) {
          setThemeData(data.theme);
        }
      } catch (error) {

      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchTheme();
    }
  }, [user]);


  const handleColorChange = (field: keyof ThemeData, value: string) => {
    // Sanitize hex input: only allow 0-9, a-f, A-F, max 6 chars
    const sanitizedValue = value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
    setThemeData(prev => ({ ...prev, [field]: sanitizedValue }));
  };


  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const formData = new FormData();
      formData.append('name', themeData.name);
      formData.append('primaryColor', themeData.primaryColor);
      formData.append('secondaryColor', themeData.secondaryColor);
      formData.append('accentColor', themeData.accentColor);
      formData.append('backgroundColor', themeData.backgroundColor);
      formData.append('textPrimary', themeData.textPrimary);
      formData.append('textSecondary', themeData.textSecondary);

      if (selectedFile) {
        formData.append('logo', selectedFile);
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/consultant/theme`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Theme saved successfully!');
        setTimeout(() => setSuccess(null), 2000);
      } else {
        setError(data.message || 'Failed to save theme');
      }
    } catch (err) {
      setError('Failed to save theme.');
    } finally {
      setIsSaving(false);
    }
  };


  const handleCancel = () => {
    setThemeData(getThemeDefaults());
    setSelectedFile(null);
  };


  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };


  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };


  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };


  const handleRemoveLogo = () => {
    setSelectedFile(null);
  };


  const handleSwatchClick = (field: keyof ThemeData) => {
    setActiveColorField(field);
    colorPickerRef.current?.click();
  };


  const handleColorPickerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (activeColorField) {
      const hexValue = event.target.value.replace('#', '');
      handleColorChange(activeColorField, hexValue);
    }
  };


  return (
    <div className={`consultant-theme ${isDarkMode ? 'dark' : ''}`}>
      {error && (
        <div className="consultant-theme__alert consultant-theme__alert--error">
          <span className="material-symbols-outlined">error</span>
          <p>{error}</p>
          <button onClick={() => setError(null)}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      )}
      {success && (
        <div className="consultant-theme__alert consultant-theme__alert--success">
          <span className="material-symbols-outlined">check_circle</span>
          <p>{success}</p>
        </div>
      )}
      {isLoading ? (
        <div className="consultant-theme__loading">
          <div className="consultant-theme__spinner"></div>
          <p>Loading theme...</p>
        </div>
      ) : isSaving ? (
        <div className="consultant-theme__loading">
          <div className="consultant-theme__spinner"></div>
          <p>Saving theme...</p>
        </div>
      ) : (
        <div className="consultant-theme__container">
          <main className="consultant-theme__main">
            <div className="consultant-theme__content">
              <header className="consultant-theme__header">
                <div className="consultant-theme__header-content">
                  <h1 className="consultant-theme__title">Theme Customization</h1>
                  <p className="consultant-theme__description">
                    Customize your consultant theme colors and logo.
                  </p>
                </div>
                <div className="consultant-theme__header-actions">
                  <button
                    className="consultant-theme__cancel-btn"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    <span className="material-symbols-outlined">refresh</span>
                    Reset
                  </button>
                  <button
                    className="consultant-theme__save-btn"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    <span className="material-symbols-outlined consultant-theme__save-icon">
                      {isSaving ? 'hourglass_empty' : 'save'}
                    </span>
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </header>
              <div className="consultant-theme__tab-selector">
                <button
                  className={`consultant-theme__tab-btn ${activeTab === 'palette' ? 'consultant-theme__tab-btn--active' : ''}`}
                  onClick={() => setActiveTab('palette')}
                >
                  Color Palette
                </button>
                <button
                  className={`consultant-theme__tab-btn ${activeTab === 'logo' ? 'consultant-theme__tab-btn--active' : ''}`}
                  onClick={() => setActiveTab('logo')}
                >
                  Logo
                </button>
              </div>
              {activeTab === 'palette' && (
                <section className="consultant-theme__section">
                  <div className="consultant-theme__section-header">
                    <span className="material-symbols-outlined consultant-theme__section-icon">
                      palette
                    </span>
                    <h2 className="consultant-theme__section-title">Color Customization</h2>
                  </div>
                  <div className="consultant-theme__color-grid">
                    {/* Primary Color */}
                    <div className="consultant-theme__color-item">
                      <div
                        className="consultant-theme__color-swatch consultant-theme__color-swatch--primary consultant-theme__color-swatch--clickable"
                        onClick={() => handleSwatchClick('primaryColor')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleSwatchClick('primaryColor')}
                      >
                        <div
                          className="consultant-theme__color-preview"
                          style={{ backgroundColor: `#${themeData.primaryColor}` }}
                        ></div>
                      </div>
                      <div className="consultant-theme__color-input-group">
                        <label className="consultant-theme__color-label">Primary Color</label>
                        <div className="consultant-theme__color-input-wrapper">
                          <span className="consultant-theme__color-hash">#</span>
                          <input
                            type="text"
                            className="consultant-theme__color-input"
                            value={themeData.primaryColor}
                            onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                          />
                          <button className="consultant-theme__color-picker-btn">
                            <span className="material-symbols-outlined">colorize</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Secondary Color */}
                    <div className="consultant-theme__color-item">
                      <div
                        className="consultant-theme__color-swatch consultant-theme__color-swatch--clickable"
                        onClick={() => handleSwatchClick('secondaryColor')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleSwatchClick('secondaryColor')}
                      >
                        <div
                          className="consultant-theme__color-preview"
                          style={{ backgroundColor: `#${themeData.secondaryColor}` }}
                        ></div>
                      </div>
                      <div className="consultant-theme__color-input-group">
                        <label className="consultant-theme__color-label">Secondary Color</label>
                        <div className="consultant-theme__color-input-wrapper">
                          <span className="consultant-theme__color-hash">#</span>
                          <input
                            type="text"
                            className="consultant-theme__color-input"
                            value={themeData.secondaryColor}
                            onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    {/* Accent Color */}
                    <div className="consultant-theme__color-item">
                      <div
                        className="consultant-theme__color-swatch consultant-theme__color-swatch--clickable"
                        onClick={() => handleSwatchClick('accentColor')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleSwatchClick('accentColor')}
                      >
                        <div
                          className="consultant-theme__color-preview"
                          style={{ backgroundColor: `#${themeData.accentColor}` }}
                        ></div>
                      </div>
                      <div className="consultant-theme__color-input-group">
                        <label className="consultant-theme__color-label">Accent Color</label>
                        <div className="consultant-theme__color-input-wrapper">
                          <span className="consultant-theme__color-hash">#</span>
                          <input
                            type="text"
                            className="consultant-theme__color-input"
                            value={themeData.accentColor}
                            onChange={(e) => handleColorChange('accentColor', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    {/* Background Color */}
                    <div className="consultant-theme__color-item">
                      <div
                        className="consultant-theme__color-swatch consultant-theme__color-swatch--background consultant-theme__color-swatch--clickable"
                        onClick={() => handleSwatchClick('backgroundColor')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleSwatchClick('backgroundColor')}
                      >
                        <div
                          className="consultant-theme__color-preview"
                          style={{ backgroundColor: `#${themeData.backgroundColor}` }}
                        ></div>
                      </div>
                      <div className="consultant-theme__color-input-group">
                        <label className="consultant-theme__color-label">Background Color</label>
                        <div className="consultant-theme__color-input-wrapper">
                          <span className="consultant-theme__color-hash">#</span>
                          <input
                            type="text"
                            className="consultant-theme__color-input"
                            value={themeData.backgroundColor}
                            onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    {/* Text Primary */}
                    <div className="consultant-theme__color-item">
                      <div
                        className="consultant-theme__color-swatch consultant-theme__color-swatch--clickable"
                        onClick={() => handleSwatchClick('textPrimary')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleSwatchClick('textPrimary')}
                      >
                        <div
                          className="consultant-theme__color-preview"
                          style={{ backgroundColor: `#${themeData.textPrimary}` }}
                        ></div>
                      </div>
                      <div className="consultant-theme__color-input-group">
                        <label className="consultant-theme__color-label">Text Primary</label>
                        <div className="consultant-theme__color-input-wrapper">
                          <span className="consultant-theme__color-hash">#</span>
                          <input
                            type="text"
                            className="consultant-theme__color-input"
                            value={themeData.textPrimary}
                            onChange={(e) => handleColorChange('textPrimary', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    {/* Text Secondary */}
                    <div className="consultant-theme__color-item">
                      <div
                        className="consultant-theme__color-swatch consultant-theme__color-swatch--clickable"
                        onClick={() => handleSwatchClick('textSecondary')}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleSwatchClick('textSecondary')}
                      >
                        <div
                          className="consultant-theme__color-preview"
                          style={{ backgroundColor: `#${themeData.textSecondary}` }}
                        ></div>
                      </div>
                      <div className="consultant-theme__color-input-group">
                        <label className="consultant-theme__color-label">Text Secondary</label>
                        <div className="consultant-theme__color-input-wrapper">
                          <span className="consultant-theme__color-hash">#</span>
                          <input
                            type="text"
                            className="consultant-theme__color-input"
                            value={themeData.textSecondary}
                            onChange={(e) => handleColorChange('textSecondary', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              )}
              {activeTab === 'logo' && (
                <section className="consultant-theme__section">
                  <div className="consultant-theme__section-header">
                    <span className="material-symbols-outlined consultant-theme__section-icon">
                      image
                    </span>
                    <h2 className="consultant-theme__section-title">Logo Management</h2>
                  </div>
                  <div className="consultant-theme__logo-grid">
                    {/* Upload Area */}
                    <div className="consultant-theme__upload-area">
                      <label className="consultant-theme__upload-label">Upload Logo</label>
                      <div
                        className="consultant-theme__upload-zone"
                        onDrop={handleFileDrop}
                        onDragOver={handleDragOver}
                      >
                        <div className="consultant-theme__upload-icon">
                          <span className="material-symbols-outlined">cloud_upload</span>
                        </div>
                        <p className="consultant-theme__upload-text">
                          Drag & drop or{' '}
                          <label className="consultant-theme__upload-link">
                            click to upload
                            <input
                              type="file"
                              className="consultant-theme__file-input"
                              onChange={handleFileSelect}
                              accept="image/*"
                            />
                          </label>
                        </p>
                        <p className="consultant-theme__upload-hint">
                          Format: PNG, JPG, SVG • Max size: 2MB
                        </p>
                      </div>
                    </div>
                    {/* Logo Preview */}
                    <div className="consultant-theme__logo-preview">
                      {(selectedFile || themeData.logoUrl) && (
                        <div className="consultant-theme__logo-item">
                          <div className="consultant-theme__logo-thumb">
                            <img
                              src={selectedFile ? URL.createObjectURL(selectedFile) : themeData.logoUrl}
                              alt="Logo Preview"
                              className="consultant-theme__logo-image"
                            />
                          </div>
                          <div className="consultant-theme__logo-info">
                            <p className="consultant-theme__logo-name">
                              {selectedFile ? selectedFile.name : themeData.logoName}
                            </p>
                            <p className="consultant-theme__logo-meta">
                              {selectedFile
                                ? `${Math.round(selectedFile.size / 1024)}KB`
                                : themeData.logoSize
                              } • {selectedFile ? 'Uploaded just now' : themeData.logoUploadedAt}
                            </p>
                          </div>
                          <button
                            className="consultant-theme__logo-remove"
                            onClick={handleRemoveLogo}
                          >
                            <span className="material-symbols-outlined">delete</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}
            </div>
          </main>
          {/* Live Preview Sidebar */}
          <aside
            className="consultant-theme__sidebar"
            style={{
              '--theme-primary': `#${themeData.primaryColor}`,
              '--theme-secondary': `#${themeData.secondaryColor}`,
              '--theme-accent': `#${themeData.accentColor}`,
              '--theme-background': `#${themeData.backgroundColor}`,
              '--theme-text-primary': `#${themeData.textPrimary}`,
              '--theme-text-secondary': `#${themeData.textSecondary}`,
            } as React.CSSProperties}
          >
            <div className="consultant-theme__preview-header">
              <h3 className="consultant-theme__preview-title">Live Preview</h3>
              <div className="consultant-theme__preview-controls">
                <button className="consultant-theme__preview-btn consultant-theme__preview-btn--active">
                  <span className="material-symbols-outlined">desktop_windows</span>
                </button>
                <button className="consultant-theme__preview-btn">
                  <span className="material-symbols-outlined">smartphone</span>
                </button>
              </div>
            </div>
            <div className="consultant-theme__preview-content">
              {/* Rich Preview Playground */}
              <div className="consultant-theme__preview-playground">
                <h4 className="consultant-theme__preview-playground-title">Theme Preview</h4>
                {/* Button Row */}
                <div className="consultant-theme__preview-buttons">
                  <button className="consultant-theme__preview-btn consultant-theme__preview-btn--primary">
                    Primary Action
                  </button>
                  <button className="consultant-theme__preview-btn consultant-theme__preview-btn--secondary">
                    Secondary Action
                  </button>
                </div>
                {/* Badge */}
                <div className="consultant-theme__preview-badge">
                  <span className="consultant-theme__preview-badge-text">Accent Badge</span>
                </div>
                {/* Sample Text */}
                <div className="consultant-theme__preview-text-sample">
                  <h5 className="consultant-theme__preview-text-primary">Primary Text</h5>
                  <p className="consultant-theme__preview-text-secondary">Secondary text for additional information</p>
                </div>
                {/* Progress Bar */}
                <div className="consultant-theme__preview-progress">
                  <div className="consultant-theme__preview-progress-label">Progress</div>
                  <div className="consultant-theme__preview-progress-bar">
                    <div className="consultant-theme__preview-progress-fill"></div>
                  </div>
                </div>
              </div>
              {/* Mock Report Preview */}
              <div className="consultant-theme__preview-card">
                {/* Header */}
                <div className="consultant-theme__preview-card-header">
                  <div className="consultant-theme__preview-card-header-left">
                    <div className="consultant-theme__preview-icon consultant-theme__preview-icon--primary">
                      <span className="material-symbols-outlined">token</span>
                    </div>
                    <div className="consultant-theme__preview-text-line consultant-theme__preview-text-line--title"></div>
                  </div>
                  <div className="consultant-theme__preview-card-header-right">
                    <div className="consultant-theme__preview-avatar"></div>
                  </div>
                </div>
                {/* Content */}
                <div className="consultant-theme__preview-card-content">
                  <div className="consultant-theme__preview-content-header">
                    <div className="consultant-theme__preview-text-group">
                      <div className="consultant-theme__preview-text-line consultant-theme__preview-text-line--large"></div>
                      <div className="consultant-theme__preview-text-line consultant-theme__preview-text-line--medium"></div>
                    </div>
                    <button className="consultant-theme__preview-export-btn">Export Report</button>
                  </div>
                  {/* Metrics Grid */}
                  <div className="consultant-theme__preview-metrics">
                    <div className="consultant-theme__preview-metric">
                      <div className="consultant-theme__preview-metric-icon">
                        <span className="material-symbols-outlined">ecg_heart</span>
                      </div>
                      <div className="consultant-theme__preview-text-line consultant-theme__preview-text-line--small"></div>
                      <div className="consultant-theme__preview-text-line consultant-theme__preview-text-line--large"></div>
                    </div>
                    <div className="consultant-theme__preview-metric">
                      <div className="consultant-theme__preview-metric-icon consultant-theme__preview-metric-icon--accent">
                        <span className="material-symbols-outlined">bolt</span>
                      </div>
                      <div className="consultant-theme__preview-text-line consultant-theme__preview-text-line--small"></div>
                      <div className="consultant-theme__preview-text-line consultant-theme__preview-text-line--large"></div>
                    </div>
                  </div>
                  {/* Chart Section */}
                  <div className="consultant-theme__preview-chart">
                    <div className="consultant-theme__preview-chart-header">
                      <div className="consultant-theme__preview-text-line consultant-theme__preview-text-line--medium"></div>
                      <div className="consultant-theme__preview-toggle"></div>
                    </div>
                    <div className="consultant-theme__preview-chart-bars">
                      <div className="consultant-theme__preview-chart-bar"></div>
                      <div className="consultant-theme__preview-chart-bar"></div>
                      <div className="consultant-theme__preview-chart-bar consultant-theme__preview-chart-bar--current"></div>
                      <div className="consultant-theme__preview-chart-bar"></div>
                      <div className="consultant-theme__preview-chart-bar"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="consultant-theme__preview-note">
                <p>Preview updates in real-time</p>
              </div>
            </div>
          </aside>
          {/* Hidden color picker */}
          <input
            ref={colorPickerRef}
            type="color"
            className="consultant-theme__color-picker-input"
            onChange={handleColorPickerChange}
            value={activeColorField ? `#${themeData[activeColorField]}` : '#000000'}
          />
        </div>
      )}
    </div>
  );
};

export default ConsultantThemePage;


